import { logger } from '@luna/logger';
import { flattenObject } from '@luna/utils';

import { getTackerRouterOptions } from './trackerRouterOptionsSingleton';
import { TrackingEvent } from './model/TrackingEvent';
import { getPosthogTracker } from './PosthogService';
import { getBeanCounterTracker } from './BeancounterService';
import { getTrackers } from './StatisticsService';

/**
 * A tracking event is composed of a few elements:
 * - category: payment-form; watchdogs; messenger; temporary-messages
 * - name: activate; deactivate; close-additional-info; open-description
 * - data: flat object with what we want to track and good property names
 * - metadata: added automatically (source, url, normalised url, timestamp, etc.)
 *
 * Tracking events must be harmonised between mobile & web.
 * Documentation example: https://confluence.beeone.at/display/GEORGE/George+App+Tracking
 */

export async function trackEventWithData<T extends Record<string, any>>(
    category: string,
    name: string,
    data: T,
): Promise<void> {
    const { getRoute, getNormalizedRoute } = getTackerRouterOptions();

    const tracker = getBeanCounterTracker();
    const trackingEvent = new TrackingEvent(category, name, getRoute(), {
        category,
        normalized: getNormalizedRoute(),
        ...flattenObject(data, '_'),
    });

    const isLuxEvent = 'eventKey' in trackingEvent.payload && 'eventType' in trackingEvent.payload;
    if (isLuxEvent) {
        await getTrackers()
            .luxTracker.track(TrackingEvent.copy(trackingEvent, flattenObject(data, '_')))
            .catch((e) => logger.warn('Could not track lux event', e));
        return;
    }

    const postHogTracker = getPosthogTracker();

    await Promise.all([
        tracker.track(trackingEvent).catch((e) => logger.warn('Could not track event', e)),
        postHogTracker
            .track(TrackingEvent.copy(trackingEvent, data))
            .catch((e) => logger.warn('Could not track posthog event', e)),
    ]);
}
